import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Popover, Button, Form, Row, Col, InputGroup, FormControl, ButtonGroup } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { snakeCase } from 'lodash'
import { useAccountCodes } from 'api/hooks/use-account-codes'
import { useDebounce } from 'api/hooks/use-debounce'

// The params key an account segment's selected code ids live under, e.g. 'seg_one' -> 'segOnes'.
export const accountCodeType = (accountType) =>
  ({
    org: 'orgs',
    object: 'objects',
    project: 'projects',
    seg_one: 'segOnes',
    seg_two: 'segTwos',
    seg_three: 'segThrees',
    seg_four: 'segFours',
    seg_five: 'segFives',
    seg_six: 'segSixes',
    seg_seven: 'segSevens',
    seg_eight: 'segEights'
  })[accountType] || `${accountType}s`

// Search, include/exclude and a paged checkbox list of one segment's account codes, rendered as
// popover bodies. The caller owns the selection; this owns the search and the loaded pages.
// Shared by the grid's account column headers and the AND/OR group builder.
const AccountCodePicker = ({
  accountType,
  selectedIds = [],
  operator = 'include',
  onToggleCode,
  onToggleOperator,
  onClearAll = null,
  initialSelected = [],
  initialSearch = '',
  onSearchChange = null,
  queryParams = {},
  canEdit = true,
  actions = null
}) => {
  const codeType = accountCodeType(accountType)
  const [search, setSearch] = useState(initialSearch)
  const [page, setPage] = useState(1)
  const [allData, setAllData] = useState([])
  // Selected codes stay listed whatever the search, so keep each one once it has been seen.
  const [selectedItems, setSelectedItems] = useState(initialSelected)
  const debouncedSearch = useDebounce(search, 500)

  const { data, isSuccess, isFetching } = useAccountCodes({
    params: {
      ...queryParams,
      code_type: codeType,
      page,
      perPage: 20,
      [`${snakeCase(codeType)}_search`]: debouncedSearch
    },
    options: { keepPreviousData: true }
  })

  useEffect(() => {
    if (!isSuccess || !data) return
    const newData = data.data || data
    setAllData((prev) => {
      if (page === 1) return newData
      const existingIds = new Set(prev.map((item) => item.id))
      return [...prev, ...newData.filter((item) => !existingIds.has(item.id))]
    })
    const seenSelected = newData.filter((item) => selectedIds.includes(item.id))
    if (seenSelected.length) {
      setSelectedItems((prev) => [...prev.filter((item) => !seenSelected.some((s) => s.id === item.id)), ...seenSelected])
    }
  }, [data, isSuccess, page]) // eslint-disable-line react-hooks/exhaustive-deps

  // Page 1 of the new search replaces the list when it arrives. Emptying it here instead lost a
  // cached page, which react-query hands back before this effect runs.
  useEffect(() => {
    setPage(1)
    onSearchChange?.(debouncedSearch)
  }, [debouncedSearch]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleCode = (accountCode, checked) => {
    if (!canEdit) return
    setSelectedItems((prev) =>
      checked ? [...prev.filter((item) => item.id !== accountCode.id), accountCode] : prev.filter((item) => item.id !== accountCode.id)
    )
    onToggleCode(accountCode, checked)
  }

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target
    if (scrollHeight - scrollTop <= clientHeight + 50 && !isFetching && (data?.data || data)?.length > 0) {
      setPage((prev) => prev + 1)
    }
  }

  const selected = selectedItems.filter((item) => selectedIds.includes(item.id))
  const searchTerm = debouncedSearch?.trim().toLowerCase()
  const unselected = [...new Map(allData.filter((item) => !selectedIds.includes(item.id)).map((item) => [item.id, item])).values()]
    .filter((item) =>
      !searchTerm ||
      [item.name, item.code, item.external_id].some((value) => value?.toLowerCase().includes(searchTerm))
    )

  const codeCheck = (accountCode, checked) => (
    <Form.Check
      key={`${checked ? 'selected-' : ''}${accountCode.id}`}
      type="checkbox"
      id={`filter-account-code-${checked ? 'selected-' : ''}${accountType}-${accountCode.id}`}
      className="mb-1"
      label={
        <>
          <span>{accountCode.name}</span> <span className="text-muted">{accountCode.code}</span>
        </>
      }
      checked={checked}
      onChange={(e) => toggleCode(accountCode, e.target.checked)}
      disabled={!canEdit}
    />
  )

  const operatorButton = (value, label) => (
    <Button
      className={value === 'include' ? 'border-1 ms-2' : 'border-1'}
      size="sm"
      variant={operator === value ? 'primary' : 'bg-white border border-primary'}
      disabled={!canEdit}
    >
      {label}
    </Button>
  )

  return (
    <>
      <Popover.Body className="bg-light">
        <Row className="gx-1">
          <Col>
            <InputGroup>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
              <FormControl
                placeholder="Search by ID or Name"
                size="sm"
                value={search}
                onChange={(e) => canEdit && setSearch(e.target.value)}
                disabled={!canEdit}
              />
            </InputGroup>
          </Col>
          <Col xs="auto">
            <ButtonGroup onClick={() => canEdit && onToggleOperator()}>
              {operatorButton('include', 'Include')}
              {operatorButton('exclude', 'Exclude')}
            </ButtonGroup>
          </Col>
          {actions && <Col xs="auto">{actions}</Col>}
        </Row>
      </Popover.Body>
      <hr className="m-0" />
      <Popover.Body style={{ maxHeight: '300px', overflowY: 'auto' }} onScroll={handleScroll}>
        {selected.length > 0 && (
          <>
            <div className="mb-2 d-flex align-items-center justify-content-between">
              <small className="text-muted fw-bold">
                {`${operator}d`.toUpperCase()} ACCOUNTS ({selected.length})
              </small>
              {onClearAll && (
                <Button variant="link" size="sm" className="p-0" onClick={onClearAll} disabled={!canEdit}>
                  Clear All
                </Button>
              )}
            </div>
            {selected.map((accountCode) => codeCheck(accountCode, true))}
            <hr className="my-2" />
          </>
        )}
        <div>
          {unselected.length === 0 && !isFetching && (
            <div className="text-center">No accounts found. Clear filters to expand your search.</div>
          )}
          {unselected.map((accountCode) => codeCheck(accountCode, false))}
          {isFetching && (
            <div className="text-center py-2">
              <small className="text-muted">Loading more...</small>
            </div>
          )}
        </div>
      </Popover.Body>
    </>
  )
}

AccountCodePicker.propTypes = {
  accountType: PropTypes.string.isRequired,
  selectedIds: PropTypes.array,
  operator: PropTypes.string,
  onToggleCode: PropTypes.func.isRequired,
  onToggleOperator: PropTypes.func.isRequired,
  onClearAll: PropTypes.func,
  initialSelected: PropTypes.array,
  initialSearch: PropTypes.string,
  onSearchChange: PropTypes.func,
  queryParams: PropTypes.object,
  canEdit: PropTypes.bool,
  actions: PropTypes.node
}

export default AccountCodePicker
